import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { driver, type Config, type DriveStep, type Driver } from 'driver.js';
import { cardInfoDataFromDefinition } from '../ui/CardInfoCard.js';
import { CatalogCard } from '../ui/CatalogCard.js';
import { Button, Modal } from '../ui/kit/index.js';
import type { FastOnboardingProgress, OnboardingApi } from './api.js';

const DECK_EDITOR_STEPS: readonly DriveStep[] = [
  {
    element: '[data-onboarding="ai-assistant"]',
    popover: {
      title: 'ИИ поможет с колодой',
      description: 'Попросите помощника собрать или изменить колоду. Нажмите сюда и опишите стиль игры.',
      side: 'left',
    },
  },
];

export const DECK_EDITOR_AI_TOUR_STORAGE_KEY = 'cards:onboarding:deck-editor-ai-tour:v1';

const GAME_UI_STEPS: readonly DriveStep[] = [
  {
    element: '[data-onboarding="opponent-status"]',
    popover: {
      title: 'Цель боя',
      description: 'Победите, снизив здоровье соперника до нуля.',
      side: 'bottom',
    },
  },
  {
    element: '[data-onboarding="board"]',
    popover: {
      title: 'Поле',
      description: 'Карта бьёт карту напротив. Если ячейка пуста — соперника.',
      side: 'bottom',
    },
  },
  {
    element: '[data-onboarding="player-resources"]',
    popover: {
      title: 'Ваши ресурсы',
      description: 'Здесь: ваше здоровье, энергия и карты в колоде.',
      side: 'top',
    },
  },
  {
    element: '[data-onboarding="hand"]',
    popover: {
      title: 'Рука',
      description: 'Цена карты — нужная энергия. Выберите карту и ячейку или перетащите карту на поле.',
      side: 'top',
    },
  },
  {
    element: '[data-onboarding="plan"]',
    popover: {
      title: 'Очередь заклинаний',
      description: 'Заклинания ждут здесь. Линии показывают цели. Наведите, чтобы прочитать карту; нажмите, чтобы отменить.',
      side: 'top',
    },
  },
  {
    element: '[data-onboarding="ready"]',
    popover: {
      title: 'Завершить подготовку',
      description: 'Вы готовите ход одновременно. Когда оба нажмут «Готов», ход разыграется.',
      side: 'top',
    },
  },
  {
    element: '[data-onboarding="header-home"]',
    popover: {
      title: 'Главная',
      description: 'Отсюда можно вернуться на главный экран.',
      side: 'bottom',
    },
  },
];

const LOBBY_STEPS: readonly DriveStep[] = [
  {
    element: '[data-onboarding="lobby-search"]',
    popover: {
      title: 'Найти соперника',
      description: 'Сыграть с другим игроком.',
      side: 'bottom',
    },
  },
  {
    element: '[data-onboarding="lobby-bot"]',
    popover: {
      title: 'Играть с ботом',
      description: 'Тренировочный матч с вашей колодой.',
      side: 'top',
    },
  },
  {
    element: '[data-onboarding="lobby-challenges"]',
    popover: {
      title: 'Продолжить обучение',
      description: 'Челленджи объясняют механики и открывают карты.',
      side: 'top',
    },
  },
  {
    element: '[data-onboarding="header-decks"]',
    popover: {
      title: 'Колоды',
      description: 'Создавайте, меняйте и выбирайте колоды.',
      side: 'bottom',
    },
  },
  {
    element: '[data-onboarding="ai-assistant"]',
    popover: {
      title: 'ИИ-помощник',
      description: 'Не знаете, что делать? Откройте чат: он объяснит этот экран и правила.',
      side: 'left',
    },
  },
];

function baseConfig(
  steps: readonly DriveStep[],
  complete: () => Promise<void>,
  requestSkip: (finish: () => void) => void,
): Config {
  let completing = false;
  const multiStep = steps.length > 1;
  const finish = (tour: Driver): void => {
    if (completing) return;
    completing = true;
    const popover = document.querySelector<HTMLElement>('.cards-onboarding-popover');
    popover?.setAttribute('aria-busy', 'true');
    void complete().then(() => {
      tour.destroy();
    }, () => {
      completing = false;
      popover?.removeAttribute('aria-busy');
      const description = popover?.querySelector<HTMLElement>('.driver-popover-description');
      if (description !== null && description !== undefined) {
        description.textContent = 'Не удалось сохранить прогресс. Проверьте соединение и нажмите «Понятно» ещё раз.';
      }
    });
  };
  return {
    steps: [...steps],
    animate: true,
    allowClose: false,
    allowScroll: true,
    allowKeyboardControl: true,
    overlayClickBehavior: () => undefined,
    disableActiveInteraction: true,
    skipMissingElement: false,
    showButtons: multiStep ? ['previous', 'next'] : ['next'],
    showProgress: multiStep,
    progressText: '{{current}} / {{total}}',
    nextBtnText: 'Далее',
    prevBtnText: 'Назад',
    doneBtnText: 'Понятно',
    popoverClass: 'cards-onboarding-popover',
    onPopoverRender: (popover) => {
      popover.closeButton.style.display = 'block';
      popover.closeButton.setAttribute('aria-label', 'Пропустить обучение');
      popover.closeButton.title = 'Пропустить обучение';
    },
    onCloseClick: (_element: Element | undefined, _step: DriveStep, options: { driver: Driver }) => {
      requestSkip(() => finish(options.driver));
    },
    onDoneClick: (_element: Element | undefined, _step: DriveStep, options: { driver: Driver }) => {
      finish(options.driver);
    },
  };
}

function useTour(
  enabled: boolean,
  steps: readonly DriveStep[],
  complete: () => Promise<void>,
  requestSkip: (finish: () => void) => void,
): void {
  const activeRef = useRef<Driver | undefined>(undefined);
  const completeRef = useRef(complete);
  const requestSkipRef = useRef(requestSkip);
  completeRef.current = complete;
  requestSkipRef.current = requestSkip;

  useEffect(() => {
    if (!enabled || activeRef.current !== undefined) return undefined;
    const instance = driver(baseConfig(
      steps,
      () => completeRef.current(),
      (finish) => requestSkipRef.current(finish),
    ));
    activeRef.current = instance;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled && activeRef.current === instance) instance.drive();
    });
    return () => {
      cancelled = true;
      if (activeRef.current === instance) {
        instance.destroy();
        activeRef.current = undefined;
      }
    };
  }, [enabled, steps]);
}

function TourSkipConfirmation({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}): ReactElement {
  return (
    <Modal
      as="section"
      ariaLabel="Пропустить обучение"
      layer="confirm"
      portal
      overlayClassName="onboarding-skip-confirm-overlay"
      className="onboarding-skip-confirm"
      onClose={onCancel}
    >
      <h2>Пропустить обучение?</h2>
      <p>Его можно будет пройти позже в челленджах.</p>
      <Modal.Actions className="onboarding-skip-confirm-actions">
        <Button variant="secondary" size="compact" onClick={onCancel}>Остаться</Button>
        <Button variant="primary" size="compact" onClick={onConfirm}>Пропустить</Button>
      </Modal.Actions>
    </Modal>
  );
}

function useSkipConfirmation(): {
  readonly requestSkip: (finish: () => void) => void;
  readonly confirmation: ReactElement | null;
} {
  const [finish, setFinish] = useState<(() => void)>();
  const requestSkip = useCallback((nextFinish: () => void) => {
    setFinish(() => nextFinish);
  }, []);
  const cancel = useCallback(() => setFinish(undefined), []);
  const confirm = useCallback(() => {
    const pending = finish;
    setFinish(undefined);
    pending?.();
  }, [finish]);
  return {
    requestSkip,
    confirmation: finish === undefined
      ? null
      : <TourSkipConfirmation onCancel={cancel} onConfirm={confirm} />,
  };
}

export interface TourMarkerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function DeckEditorOnboardingTour({
  storage,
}: {
  storage?: TourMarkerStorage;
}): ReactElement | null {
  const [eligible, setEligible] = useState(false);
  const storageRef = useRef<TourMarkerStorage | undefined>(storage);
  const { requestSkip, confirmation } = useSkipConfirmation();

  useEffect(() => {
    let resolvedStorage = storage;
    try {
      resolvedStorage ??= globalThis.localStorage;
      storageRef.current = resolvedStorage;
      setEligible(
        resolvedStorage.getItem(DECK_EDITOR_AI_TOUR_STORAGE_KEY) !== '1'
        && document.querySelector('[data-onboarding="ai-assistant"]') !== null,
      );
    } catch {
      storageRef.current = undefined;
      setEligible(false);
    }
  }, [storage]);

  useTour(eligible, DECK_EDITOR_STEPS, async () => {
    try {
      storageRef.current?.setItem(DECK_EDITOR_AI_TOUR_STORAGE_KEY, '1');
    } catch {
      // Browser storage is an optional repeat-suppression hint.
    }
  }, requestSkip);
  return confirmation;
}

export interface GameOnboardingTourProps {
  enabled: boolean;
  round: number;
  phase: 'planning' | 'resolution';
  playbackIdle: boolean;
  csrfToken: string;
  api: OnboardingApi;
  onCompleted: (progress: FastOnboardingProgress) => void;
}

export function GameOnboardingTour({
  enabled,
  round,
  phase,
  playbackIdle,
  csrfToken,
  api,
  onCompleted,
}: GameOnboardingTourProps): ReactElement | null {
  const [progress, setProgress] = useState<FastOnboardingProgress>();
  const [cardIntroVisible, setCardIntroVisible] = useState(false);
  const completionReportedRef = useRef(false);
  const { requestSkip, confirmation } = useSkipConfirmation();
  const gameSteps = useMemo<readonly DriveStep[]>(() => [
    {
      element: '[data-onboarding="card-anatomy"] [data-card-region="meta"]',
      disableActiveInteraction: false,
      onHighlightStarted: () => setCardIntroVisible(true),
      popover: {
        title: 'Тип и стоимость',
        description: 'Тип карты и её цена в энергии.',
        side: 'right',
      },
    },
    {
      element: '[data-onboarding="card-anatomy"] [data-card-region="stats"]',
      disableActiveInteraction: false,
      onHighlightStarted: () => setCardIntroVisible(true),
      popover: {
        title: 'Атака и здоровье',
        description: 'Атака — урон. Здоровье — сколько урона выдержит боец.',
        side: 'right',
      },
    },
    {
      element: '[data-onboarding="card-anatomy"] [data-card-region="description"]',
      disableActiveInteraction: false,
      onHighlightStarted: () => setCardIntroVisible(true),
      popover: {
        title: 'Способность',
        description: 'Здесь написано, что делает карта и когда эффект срабатывает.',
        side: 'right',
      },
    },
    {
      ...GAME_UI_STEPS[0]!,
      onHighlightStarted: () => setCardIntroVisible(false),
    },
    ...GAME_UI_STEPS.slice(1),
  ], []);

  useEffect(() => {
    if (!enabled) {
      setProgress(undefined);
      completionReportedRef.current = false;
      return undefined;
    }
    const controller = new AbortController();
    void api.get(controller.signal).then((projection) => {
      if (!controller.signal.aborted) setProgress(projection.progress);
    }, () => {
      // Keep the marker for a later reload instead of showing a tour whose
      // server-side completion state is unknown.
    });
    return () => controller.abort();
  }, [api, enabled]);

  useEffect(() => {
    if (progress?.gameTourCompletedAt === null || progress === undefined
      || completionReportedRef.current) return;
    completionReportedRef.current = true;
    onCompleted(progress);
  }, [onCompleted, progress]);

  const eligible = enabled
    && progress?.gameTourCompletedAt === null
    && round === 1
    && phase === 'planning'
    && playbackIdle;
  useEffect(() => {
    if (!eligible) setCardIntroVisible(false);
  }, [eligible]);
  useTour(eligible, gameSteps, async () => {
    const updated = await api.completeMilestone('game-tour-completed', csrfToken);
    completionReportedRef.current = true;
    onCompleted(updated);
  }, requestSkip);
  const anatomyCard = cardInfoDataFromDefinition('shield_bearer');
  return (
    <>
      {confirmation}
      <div
        className="onboarding-card-anatomy"
        data-active={cardIntroVisible ? 'true' : 'false'}
        data-onboarding="card-anatomy"
        data-testid="onboarding-card-anatomy"
        aria-hidden={!cardIntroVisible}
      >
        {anatomyCard === undefined ? null : (
          <CatalogCard
            data={anatomyCard}
            testId="onboarding-card-anatomy-example"
          />
        )}
      </div>
    </>
  );
}

export interface LobbyOnboardingTourProps {
  progress: FastOnboardingProgress;
  csrfToken: string;
  api: OnboardingApi;
  onCompleted: (progress: FastOnboardingProgress) => void;
}

export function LobbyOnboardingTour({
  progress,
  csrfToken,
  api,
  onCompleted,
}: LobbyOnboardingTourProps): ReactElement | null {
  const eligible = progress.onboardingSoloStartedAt !== null
    && progress.lobbyTourCompletedAt === null;
  const { requestSkip, confirmation } = useSkipConfirmation();
  useTour(eligible, LOBBY_STEPS, async () => {
    const updated = await api.completeMilestone('lobby-tour-completed', csrfToken);
    onCompleted(updated);
  }, requestSkip);
  return confirmation;
}
